import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import { userService } from './userService';

const toSharablUser = (firebaseUser, profile = null, profileError = null) => ({
  uid: firebaseUser.uid,
  email: firebaseUser.email,
  displayName: profile?.displayName || firebaseUser.displayName,
  photoURL: profile?.photoURL || firebaseUser.photoURL,
  profile,
  profileError,
  firebaseUser,
});

const syncSharablUserProfile = async (firebaseUser) => {
  try {
    return await userService.upsertUserProfile(firebaseUser);
  } catch (error) {
    console.warn('Signed in, but Sharabl user profile sync failed.', error);
    return null;
  }
};

const saveSharablUserProfile = async (firebaseUser, profile) => userService.upsertUserProfile(firebaseUser, profile);

export const authService = {
  onAuthChanged(callback) {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        callback(null);
        return;
      }

      const profile = await syncSharablUserProfile(firebaseUser);
      callback(toSharablUser(firebaseUser, profile));
    });
  },

  async signInWithEmail(email, password) {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    await syncSharablUserProfile(credential.user);
    return credential;
  },

  async registerWithEmail(email, password, displayName) {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(credential.user, { displayName });
    }
    await syncSharablUserProfile(credential.user);
    return credential;
  },

  async signInWithGoogle() {
    const credential = await signInWithPopup(auth, googleProvider);
    await syncSharablUserProfile(credential.user);
    return credential;
  },

  async updateCurrentProfile(profile) {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to update your profile.');
    }
    const authProfile = {
      displayName: profile.displayName || '',
      photoURL: profile.photoURL || '',
    };
    await updateProfile(auth.currentUser, authProfile);
    return saveSharablUserProfile(auth.currentUser, profile);
  },

  async signOut() {
    return signOut(auth);
  },
};
